import { Component, OnInit, OnDestroy, ViewEncapsulation, Input, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { Subscription } from 'rxjs';
import { IFileDocument, IFilePageAndWords } from 'src/modules/shared/models/interfaces/files.interface';
import { GroupConfiguration } from 'src/modules/shared/models/group-keys.model';
import { ISmartCanvasClickedItemEvent, ISmartCanvasOptions } from 'src/modules/shared/models/interfaces/smart-canvas.interface';
import { SmartCanvasOptions } from 'src/modules/shared/models/smart-canvas.model';
import { FilesService } from 'src/modules/shared/services/files.service';
import { RobotsService } from 'src/modules/shared/services/robots.service';
import { EventEmitterService } from 'src/modules/shared/services/event-emitter.service';
import { AuthService } from 'src/modules/authentication/services/auth.service';
import { GeneratorsUtility } from 'src/modules/shared/utilities/generators';
import { IGroupConfiguration, IGroupConfigurationKey } from 'src/modules/shared/models/interfaces/group-keys.interface';
import { FilesHelper } from 'src/modules/shared/helpers/files.helper';

@Component({
  selector: 'file-classification',
  templateUrl: './classification.component.html',
  styleUrls: ['./classification.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class ClassificationComponent implements OnInit, OnDestroy, OnChanges {
  @Input() niboToken?: string;
  @Input() fileId: string;
  @Input() robotId?: string;
  @Input() groupConfigurationKeys?: IGroupConfigurationKey[];
  @Input() options: ISmartCanvasOptions;
  @Output() clickedValue: EventEmitter<ISmartCanvasClickedItemEvent> = new EventEmitter();
  @Output() loaded: EventEmitter<void> = new EventEmitter();
  @Output() loadError: EventEmitter<void> = new EventEmitter();
  @Output() notSupportedError: EventEmitter<void> = new EventEmitter();

  public selectedFile: IFileDocument;
  public selectedGroupConfiguration: IGroupConfiguration;
  public selectedFilePagesAndWords: IFilePageAndWords[];
  public states: { [key: string]: boolean } = {
    loadingFile: true,
    errorFile: false,
  };

  private _subscriptions: { [key: string]: Subscription } = {};
  private _defaultOptions = new SmartCanvasOptions();

  constructor(
    private _filesService: FilesService,
    private _robotsService: RobotsService,
    private _eventEmitterService: EventEmitterService,
    private _authService: AuthService
  ) {
    this._defaultOptions.splitWords = false;
    this._defaultOptions.enableWordSelection = true;
    this._defaultOptions.showGroupKeys = true;
    this._defaultOptions.enableAreaSelection = false;
    this._defaultOptions.showConfidence = false;
    this._defaultOptions.showGroupKeysOnHover = true;
    this.options = this._defaultOptions;

    this._eventEmitterService.get<ISmartCanvasClickedItemEvent>('clicked-value').subscribe((click) => {
      this.clickedValue.emit(click);
    });
  }

  private _loadFileDetails(): void {
    this.states.loadingFile = true;
    this.states.errorFile = false;
    this.selectedFilePagesAndWords = null;
    this.selectedFile = null;
    this._subscriptions['loadFileDetails.getFile'] = this._filesService.getFile(this.fileId).subscribe({
      next: (file) => {
        this.selectedFile = file;
        this.states.loadingFile = false;

        if (!FilesHelper.isReaderSupported(this.selectedFile)) {
          this.notSupportedError.emit();
        }

        if (!FilesHelper.isValidForGetPagesAndWords(this.selectedFile)) return;

        this._subscriptions['loadFileDetails.getPagesAndWords'] = this._robotsService
          .getPagesAndWords(this.fileId, {
            limit: this.options.maxPagesToRead,
            offset: 0,
          })
          .subscribe((wordsAndPages) => {
            this.selectedFilePagesAndWords = wordsAndPages;

            if (this.groupConfigurationKeys) {
              this.selectedGroupConfiguration = new GroupConfiguration(GeneratorsUtility.generateGUID(), '', this.groupConfigurationKeys);
            } else if (this.robotId) {
              this._robotsService.getRobotsGroupsConfigurations(this.robotId).subscribe((result) => {
                this.selectedGroupConfiguration = result[0];
              });
            }
          });
      },
      error: () => {
        this.states.loadingFile = false;
        this.states.errorFile = true;
        this.loadError.emit();
      },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options?.currentValue) {
      this.options = { ...this._defaultOptions, ...this.options };
    }

    if (changes.niboToken?.currentValue) {
      this._authService.injectToken(this.niboToken).subscribe(() => {
        if (this.fileId) {
          this._loadFileDetails();
        }
      });
    } else if (changes.fileId?.currentValue) {
      this._loadFileDetails();
    }
  }

  ngOnInit(): void {
    if (this.fileId) {
      this._loadFileDetails();
    }
  }

  ngOnDestroy(): void {
    for (const subscription in this._subscriptions) {
      this._subscriptions[subscription].unsubscribe();
    }
  }
}
